import React, { useState } from "react";

import { faCaretDown } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, Text } from "@performyard/py-components";
import { Avatar } from "@performyard/py-components";

import FloatingDropdown from "./FloatingDropdown";
import SaveStatus from "./SaveStatus";

import { useRbacObjectPermissions } from "../../../../api/hooks";

import { config } from "../config";
import { useMeetingContext } from "../context";

type NotesDropdownHeaderProps = {
	selectedOption: "my-notes" | "participant-notes" | "meeting-transcript";
	showSaved: boolean;
	showSaving: boolean;
	onOptionSelect: (optionId: "my-notes" | "participant-notes" | "meeting-transcript") => void;
	hasError: boolean;
};

/**
 * Header component for notes section with dropdown to switch between participants' notes.
 */
const NotesDropdownHeader = ({
	selectedOption,
	showSaved,
	showSaving,
	onOptionSelect,
	hasError,
}: NotesDropdownHeaderProps): React.ReactElement => {
	const { userId, participantId, participantName, meeting, workspace, isAdminView } = useMeetingContext();
	const [isOpen, setIsOpen] = useState(false);

	const meetingCreatorId = meeting?.created?.userId || userId;
	const meetingCreatorName =
		workspace?.participants?.find((p) => p.userId === meetingCreatorId)?.name || "Meeting Owner";

	const myNotesLabel = isAdminView ? `${meetingCreatorName}'s Shared Notes` : "My Shared Notes";
	const participantNotesLabel = `${participantName || "Participant"}'s Shared Notes`;

	const handleSelect = (option: { id: string }): void => {
		onOptionSelect(option.id as "my-notes" | "participant-notes" | "meeting-transcript");
	};

	// Check if user is a participant (can view transcript)
	const { data: rbacPermissions } = useRbacObjectPermissions("meeting", meeting?._id ?? "");
	const canViewTranscript = rbacPermissions?.includes("meetings:viewTranscript");

	const dropdownOptions = [
		{
			id: "my-notes",
			label: (
				<div className="flex items-center space-x-1">
					<Avatar id={isAdminView ? meetingCreatorId : userId} size={config.layout.dropdownAvatarSize} />
					<Text size="paragraph-3" weight="regular" color="neutral-7">
						{myNotesLabel}
					</Text>
				</div>
			),
		},
		{
			id: "participant-notes",
			label: (
				<div className="flex items-center space-x-1">
					<Avatar id={participantId || ""} size={config.layout.dropdownAvatarSize} />
					<Text size="paragraph-3" weight="regular" color="neutral-7">
						{participantNotesLabel}
					</Text>
				</div>
			),
		},
		...(canViewTranscript
			? [
					{
						id: "meeting-transcript",
						label: (
							<div className="flex items-center space-x-1">
								<Text size="paragraph-3" weight="regular" color="neutral-7">
									Meeting Transcript
								</Text>
							</div>
						),
					},
				]
			: []),
	];

	const trigger = (
		<div className="flex items-center">
			<div className="flex items-center space-x-1 focus:outline-none focus:ring-0">
				{selectedOption === "meeting-transcript" ? (
					<Text
						size="paragraph-3"
						weight="semi-bold"
						dangerouslySetClassName="hover:cursor-pointer hover:underline"
					>
						Meeting Transcript
					</Text>
				) : (
					<>
						<Avatar
							size={config.layout.dropdownAvatarSize}
							id={
								selectedOption === "my-notes"
									? isAdminView
										? meetingCreatorId
										: userId
									: participantId || ""
							}
						/>
						<Text
							size="paragraph-3"
							weight="semi-bold"
							dangerouslySetClassName="hover:cursor-pointer hover:underline"
						>
							{selectedOption === "my-notes" ? myNotesLabel : participantNotesLabel}
						</Text>
					</>
				)}
			</div>
			<ButtonIcon icon={faCaretDown} size={config.iconSize.medium} onClick={() => setIsOpen((prev) => !prev)} />
		</div>
	);

	return (
		<div className="relative">
			<div className="flex items-center">
				<FloatingDropdown
					isOpen={isOpen}
					onOpenChange={setIsOpen}
					trigger={trigger}
					options={dropdownOptions}
					onSelect={handleSelect}
					placement="bottom-start"
					offsetDistance={config.dropdownOffset.notes}
					zIndex={config.zIndex.notesDropdown}
					className="rounded-lg border-[1px]"
					optionClassName="h-8 rounded-first:rounded-t-lg last:rounded-b-lg"
				/>
				<div className="ml-auto flex h-5 min-w-[60px] items-center justify-end overflow-hidden">
					{(selectedOption === "my-notes" || selectedOption === "meeting-transcript") && (
						<SaveStatus isSaving={showSaving} isSaved={showSaved} hasError={hasError} />
					)}
				</div>
			</div>
		</div>
	);
};

export default NotesDropdownHeader;
